import ValidationService, { EmailDeliverability } from '../services/validation'

const verdictCache = new Map<string, Promise<EmailDeliverability>>()
const settledVerdicts = new Map<string, EmailDeliverability>()

function cacheKey (emailAddress: string): string {
  return emailAddress.trim().toLowerCase()
}

function isDefinitive (verdict: EmailDeliverability): boolean {
  return verdict !== 'unknown'
}

/**
 * Remote deliverability check, memoised per address so a re-submit (e.g. after fixing
 * another field) does not trigger a second lookup. Only definitive verdicts are kept;
 * an `unknown` result (service unavailable) is retried on the next submit.
 */
export async function checkEmailDeliverability (emailAddress: string): Promise<EmailDeliverability> {
  const key = cacheKey(emailAddress)
  const cached = verdictCache.get(key)
  if (cached !== undefined) {
    return await cached
  }
  const pending = new ValidationService().validateEmailAddress(emailAddress)
  verdictCache.set(key, pending)
  // validateEmailAddress never rejects, so only the fulfilment branch is needed
  void pending.then(verdict => {
    if (isDefinitive(verdict)) {
      settledVerdicts.set(key, verdict)
    } else {
      verdictCache.delete(key)
    }
  })
  return await pending
}

/** Synchronous lookup of an already settled verdict, for validation runs that must not call the API. */
export function getSettledEmailDeliverability (emailAddress: string): EmailDeliverability | undefined {
  return settledVerdicts.get(cacheKey(emailAddress))
}
